import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// mui components
import { Box, Button, Grid, Typography, styled } from '@mui/material';
// components
import Comments from '../../comments';
import Reply from '../../reply';
import CommentsCount from './components/comments-count';
import LikesCount from './components/likes-count';
import PostAvatar from './components/post-avatar';
import TagsCount from './components/tags-count';
import TagsModal from './components/tags-modal';
// custom hooks'
import useToast from 'hooks/useToast';
import { useActionsPopper } from './hooks/use-actions-popper';
// api
import {
  IUpdatePostBody,
  PostVisibilityEnum,
  UploadStatusEnum,
} from 'redux-store/api/activity-feed';
// helpers'
import { isContentEmpty } from 'utils/helpers';
// icons'
import DotIcon from 'assets/icons/buttons/dot.svg';
import LikeIcon from 'assets/icons/buttons/like-icon.svg';
import ReplyIcon from 'assets/icons/buttons/reply-icon.svg';
import ShareIcon from 'assets/icons/buttons/share-icon.svg';
import UnlikeIcon from 'assets/icons/buttons/unlike-icon.svg';
// utils'
import { createContent } from 'utils/helpers';
// constants'
// styles'
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import { postCommentsAtom, selectedProjectAtom } from 'atoms';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import UploadedAudioContent from 'components/uploaded-audio-content';
import UploadedFiles from 'components/uploaded-files';
import { isAudio, isVideo } from 'components/uploaded-images/helpers';
import { useVideoThumbnails } from 'hooks/useVideoThumbnails';
import { UploadedImagesWithStatus } from 'components/uploaded-images/uploaded-images-with-status';
import { getDownloadURL, ref } from 'firebase/storage';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import { IUploadedFilesData } from 'redux-store/types';
import { storage } from 'utils/firebase';
import { getMimeTypeFromFileName } from 'utils/helpers/fileCabinetHelpers';
import { highlightSearchMatchesInHtml } from 'utils/searchHighlight';
import { RichTextContent } from 'widgets/side-panel/sections/activity-feed/old/components/rich-text-content';
import PostAudience from 'widgets/side-panel/sections/activity-feed/old/components/post-item/components/post-audience';
import Post from '../../post';
import { postItemStyles } from './styles/styles';

dayjs.extend(LocalizedFormat);

export interface IPostItemProps {
  post: IPost;
  placeOfUse: 'activity_feed' | 'post_modal';
  projectId?: number;
  mentionOptions: ICustomMentionData[];
  searchQuery: string;
  onCreateComment: (body: Partial<IPostComment>, parentPath?: string) => void;
  onUpdateComment: (body: Partial<IPostComment>) => void;
  onUpdatePost?: (body: Partial<IPost>) => void;
  onDeletePost?: (id: string) => void;
  onDeleteComment?: (id: string) => void;
  onTogglePostLike: () => void;
}

const StyledSpan = styled('span')({
  width: '3px',
  height: '3px',
  backgroundColor: '#D4D6E0',
  borderRadius: '50%',
});

const UserPost: FC<IPostItemProps> = ({
  post,
  placeOfUse,
  projectId,
  mentionOptions,
  searchQuery,
  onCreateComment,
  onUpdateComment,
  onUpdatePost,
  onDeletePost,
  onDeleteComment,
  onTogglePostLike,
}) => {
  const showToast = useToast();

  const replyRef = useRef<HTMLDivElement>(null);
  const [replyOpen, setReplyOpen] = useState(placeOfUse === 'post_modal');
  const [commentsOpen, setCommentsOpen] = useState(true);

  const comments = useAtomValue(postCommentsAtom(`${post.parentPath}/${post.id}`));

  const selectedProject = useAtomValue(selectedProjectAtom);
  const { data: userMe } = useGetMeQuery();
  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();
  const [isEditing, setIsEditing] = useState(false);
  const [videoThumbnails, setVideoThumbnails] = useState<Map<string, string>>(new Map());
  const [openTagsModal, setOpenTagsModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { popperButton } = useActionsPopper({
    placeOfUse,
    post,
    projectId: null,
    onEdit: () => {
      setIsEditing(true);
    },
    onDelete: onDeletePost,
  });

  const handleLikeUnlike = async () => {
    onTogglePostLike();
  };

  const handleToggleReply = () => {
    setReplyOpen((prev) => !prev);
  };

  const handleOpenPostModal = useCallback(() => {
    if (placeOfUse === 'activity_feed') {
      searchParams.set('postId', post.id);
      setSearchParams(searchParams);
    }
  }, [placeOfUse, post.id, searchParams, setSearchParams]);

  // Convert storage paths to download URLs for transcoded files
  useEffect(() => {
    if (!post.media) return;

    const convertPathsToURLs = async () => {
      const pathToUrlMap = new Map<string, string>();

      for (const mediaItem of post.media || []) {
        if (mediaItem.transcoded?.thumbnail) {
          if (!mediaItem.transcoded.thumbnail.startsWith('http')) {
            try {
              const storageRef = ref(storage, mediaItem.transcoded.thumbnail);
              const downloadURL = await getDownloadURL(storageRef);
              pathToUrlMap.set(mediaItem.transcoded.thumbnail, downloadURL);
            } catch {
              // Thumbnail not available, will show fallback
            }
          }
        }
        if (mediaItem.options?.previewUrl) {
          if (!mediaItem.options.previewUrl.startsWith('http')) {
            try {
              const storageRef = ref(storage, mediaItem.options.previewUrl);
              const downloadURL = await getDownloadURL(storageRef);
              pathToUrlMap.set(mediaItem.options.previewUrl, downloadURL);
            } catch {
              // Preview URL not available, will show fallback
            }
          }
        }
      }

      if (pathToUrlMap.size > 0) {
        setVideoThumbnails((prev) => {
          const updated = new Map(prev);
          pathToUrlMap.forEach((url, path) => {
            updated.set(path, url);
          });
          return updated;
        });
      }
    };

    convertPathsToURLs();
  }, [post.media]);

  const videoEntriesForThumbnails = useMemo(
    () =>
      (post.media || [])
        .filter(
          (media) =>
            isVideo(getMimeTypeFromFileName(media.name)) &&
            !media.options?.previewUrl &&
            !media.transcoded?.thumbnail &&
            media.url
        )
        .map((media) => ({ id: media.url, url: media.url })),
    [post.media]
  );

  const generatedVideoThumbnails = useVideoThumbnails(videoEntriesForThumbnails);

  const handleOpenReply = (open: boolean) => {
    setReplyOpen(open);
  };

  const handleClickOnComments = useCallback(() => {
    if (placeOfUse === 'activity_feed') {
      searchParams.set('postId', post.id);
      setSearchParams(searchParams);
      return;
    }
    if (comments.length > 0) {
      if (commentsOpen) {
        setCommentsOpen(false);
        return;
      }
      setCommentsOpen(true);
    }
  }, [comments, placeOfUse, commentsOpen, post.id, searchParams, setSearchParams]);

  const handleShare = async () => {
    await navigator.clipboard.writeText(
      `${window.location.href}${placeOfUse === 'activity_feed' ? `?postId=${post.id}` : ''}`
    );
    showToast({ type: 'success', message: 'Post link has been copied to clipboard' });
  };

  const content = useMemo(() => {
    const baseContent = createContent(post.message, post.mentions, post.hashtags, userPermissions);
    return highlightSearchMatchesInHtml(baseContent, searchQuery);
  }, [post.message, post.mentions, post.hashtags, searchQuery, userPermissions]);

  const handleUpdateDescription = useCallback(
    (mediaItemId: string, description: string) => {
      const updatedMedia = post.media?.map((mediaItem, index) => {
        if (`media-${index}` === mediaItemId) {
          return { ...mediaItem, description: description || '' };
        }
        return mediaItem;
      });
      onUpdatePost?.({ id: post.id, media: updatedMedia } as Partial<IPost>);
    },
    [post, onUpdatePost]
  );

  const postDataForUpdate: IUpdatePostBody = {
    projectId: null,
    id: Number(post.id),
    content: post.message,
    postVisibility: post.postVisibility || PostVisibilityEnum.EVERYONE,
    // uploads: post.media,
  };

  const isDescriptionEditable =
    post?.createdBy?.userId?.toString() === userMe?.id?.toString() ||
    (userPermissions?.ACTIVITY_FEED_ALL_POSTS?.canUpdate &&
      userPermissions?.ENABLE_ACTIVITY_FEED?.canUpdate);

  const renderPostInfo = (
    <Grid sx={postItemStyles.postInfoContainer}>
      <LikesCount post={post} />
      <StyledSpan />
      <CommentsCount post={post} handleClickOnComments={handleClickOnComments} />
      <StyledSpan />
      <TagsCount
        post={post}
        project={selectedProject}
        handleOpenTagsModal={() => setOpenTagsModal(true)}
      />
    </Grid>
  );

  const renderPostActions = (
    <Grid sx={postItemStyles.likeReplyContainer}>
      <Button
        size="small"
        onClick={handleLikeUnlike}
        startIcon={post?.likes?.includes(userMe?.id?.toString()) ? <UnlikeIcon /> : <LikeIcon />}
        sx={postItemStyles.actionBtn}
      >
        {post?.likes?.includes(userMe?.id?.toString()) ? 'Liked' : 'Like'}
      </Button>
      <Button
        onClick={handleToggleReply}
        size="small"
        startIcon={<ReplyIcon />}
        sx={postItemStyles.actionBtn}
      >
        Reply
      </Button>
      <Button
        onClick={handleShare}
        size="small"
        startIcon={<ShareIcon />}
        sx={postItemStyles.actionBtn}
      >
        Share
      </Button>
    </Grid>
  );

  const renderTextContent =
    content && !isContentEmpty(content) ? (
      <RichTextContent
        projectId={projectId}
        content={content}
        sx={postItemStyles.postContentContainer}
      />
    ) : null;

  const uploadedImages = useMemo(
    () =>
      post.media
        ?.filter((mediaItem) => {
          const mimeType = getMimeTypeFromFileName(mediaItem.name);
          return !isAudio(mimeType); // Include both images and videos
        })
        .map((mediaItem, index) => {
          const mimeType = getMimeTypeFromFileName(mediaItem.name);

          // Get thumbnail URL for videos
          let thumbnailUrl: string | undefined = undefined;
          if (isVideo(mimeType)) {
            if (mediaItem.transcoded?.thumbnail) {
              thumbnailUrl = mediaItem.transcoded.thumbnail.startsWith('http')
                ? mediaItem.transcoded.thumbnail
                : videoThumbnails.get(mediaItem.transcoded.thumbnail);
            }
            if (!thumbnailUrl && mediaItem.options?.previewUrl) {
              thumbnailUrl = mediaItem.options.previewUrl.startsWith('http')
                ? mediaItem.options.previewUrl
                : videoThumbnails.get(mediaItem.options.previewUrl);
            }
            if (!thumbnailUrl) {
              thumbnailUrl = generatedVideoThumbnails.get(mediaItem.url);
            }
          }

          const item = {
            id: `media-${index}`,
            url: mediaItem.url,
            name: mediaItem.name,
            mimeType,
            description: mediaItem.description || null,
            timestamp: mediaItem.timestamp || null,
            location: mediaItem.location || null,
            profile: post.createdBy
              ? {
                  id: Number(post.createdBy.userId),
                  userId: Number(post.createdBy.userId),
                  firstName: post.createdBy.displayName?.split(' ')[0] || '',
                  lastName: post.createdBy.displayName?.split(' ').slice(1).join(' ') || '',
                  role: post.createdBy.role || '',
                  avatar: post.createdBy.avatarUrl
                    ? { id: '', url: post.createdBy.avatarUrl, name: '' }
                    : null,
                }
              : null,
            createdAt: post.createdAt,
            options: {
              previewUrl: thumbnailUrl,
            },
          };
          return item;
        }) || [],
    [post.media, videoThumbnails, generatedVideoThumbnails]
  );

  const commentAudience = useMemo(() => {
    return {
      postVisibility: post?.postVisibility,
      visibility: post?.visibility,
    };
  }, [post]);

  return (
    <Grid sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
      <Grid sx={postItemStyles.container}>
        <Grid sx={postItemStyles.headerContainer}>
          <Grid sx={postItemStyles.profileContainer}>
            <PostAvatar post={post} />
            <Grid sx={postItemStyles.userInfoContainer}>
              <Typography
                sx={postItemStyles.userName}
              >{`${post.createdBy.displayName}`}</Typography>

              {post.createdBy?.role && (
                <Box sx={postItemStyles.usersIconBox}>
                  {post.postVisibility && (
                    <PostAudience post={post} projectId={selectedProject?.id} />
                  )}

                  <DotIcon />
                  <Typography sx={postItemStyles.userRole}>{post.createdBy.role}</Typography>
                </Box>
              )}
            </Grid>
          </Grid>

          <Grid sx={postItemStyles.postDateContainer}>
            <Typography
              sx={{
                ...postItemStyles.date,
                cursor: placeOfUse === 'activity_feed' ? 'pointer' : 'auto',
              }}
              onClick={handleOpenPostModal}
            >
              {`${dayjs(post?.createdAt).format('ll')} / ${dayjs(post?.createdAt).format('LT')}`}
            </Typography>
            {popperButton}
          </Grid>
        </Grid>

        {isEditing ? (
          <Post
            isExpanded={true}
            actionType="UPDATE_POST"
            projectId={parseInt(post.projectId)}
            companyId={post.companyId}
            mentionOptions={mentionOptions}
            editablePost={post}
            setOpen={setIsEditing}
            onUpdatePost={onUpdatePost}
            uploadPath={`companies/${post.companyId}/projects/${post.projectId}/activityFeed`}
          />
        ) : (
          <Grid sx={postItemStyles.container}>
            {renderTextContent}

            <UploadedImagesWithStatus
              type="activity_feed"
              visibleImagesCount={5}
              uploads={uploadedImages}
              containerStyles={{ display: 'flex', width: '100%', height: '200px' }}
              editablePostBody={postDataForUpdate}
              isDescriptionEdtable={isDescriptionEditable}
              updateDescription={handleUpdateDescription}
              isActivityFeedMedia
            />

            <UploadedAudioContent
              uploadedMultimedia={
                post.media
                  ?.filter((mediaItem) => {
                    const mimeType = getMimeTypeFromFileName(mediaItem.name);
                    return isAudio(mimeType); // Only audio, not videos
                  })
                  .map(
                    (mediaItem, index) =>
                      ({
                        id: `media-${index}`,
                        url: mediaItem.url,
                        name: mediaItem.name,
                        mimeType: getMimeTypeFromFileName(mediaItem.name),
                      }) as IUploadedFilesData
                  ) || []
              }
            />

            <UploadedFiles
              type="activity_feed"
              uploads={
                post.media
                  ?.filter((mediaItem) => {
                    const mimeType = getMimeTypeFromFileName(mediaItem.name);
                    return !isVideo(mimeType) && !isAudio(mimeType) && mediaItem.type === 'file';
                  })
                  .map((mediaItem, index) => ({
                    id: `media-${index}`,
                    url: mediaItem.url,
                    name: mediaItem.name,
                    path: mediaItem.url, // Use URL as path for backward compatibility
                    mimeType: getMimeTypeFromFileName(mediaItem.name),
                    description: mediaItem.description || null,
                    location: null,
                    timestamp: null,
                    options: null,
                    status: UploadStatusEnum.COMPLETED, // Assume completed for existing posts
                  })) || []
              }
            />

            <Grid sx={postItemStyles.actionsContainer}>
              {userMe ? renderPostActions : <Box />}
              {renderPostInfo}
            </Grid>

            {replyOpen ? (
              <Reply
                commentAudience={commentAudience}
                projectId={parseInt(post.projectId)}
                companyId={post.companyId}
                // setCommentsOpen={setCommentsOpen}
                mentionOptions={mentionOptions}
                setOpen={handleOpenReply}
                // addLocal={placeOfUse === 'activity_feed'}
                replyRef={replyRef}
                onCreateComment={onCreateComment}
                onUpdateComment={onUpdateComment}
                uploadPath={`companies/${post.companyId}/projects/${post.projectId}/activityFeed`}
              />
            ) : null}

            {commentsOpen && comments.length > 0 ? (
              <Comments
                post={post}
                placeOfUse={placeOfUse}
                comments={comments}
                searchQuery={searchQuery}
                mentionOptions={mentionOptions}
                // commentsCount={comments.length}
              />
            ) : null}
          </Grid>
        )}
      </Grid>

      <TagsModal
        open={openTagsModal}
        closeModal={() => setOpenTagsModal(false)}
        post={post}
        project={selectedProject}
      />
    </Grid>
  );
};

export default memo(UserPost);
